import type { Chapter } from '../types';
import { extensionOf } from '../lib/format';

/** Reads an inclusive byte range of the file. Injected rather than imported so
  * the parser has no Drive dependency and can be tested against a buffer. */
export type RangeReader = (start: number, end: number) => Promise<ArrayBuffer>;

/**
* Reads embedded chapter marks out of an .m4b / .m4a file.
*
* The point is to do this **without downloading the book**. MP4 is a tree of
* length-prefixed boxes, so the top level can be walked by reading 16 bytes at
* a time over HTTP Range, and only the `moov` box — metadata, typically well
* under a megabyte even for a 20-hour recording — is ever fetched in full.
*
* Nero-style `chpl` is the format virtually every audiobook tool writes, and is
* what this parses. Files that instead use a QuickTime chapter *track* return
* nothing, and the caller falls back to treating the file as one long chapter —
* playback, seeking and resume all still work, you just lose the chapter list.
*/

/** Plenty for metadata; refuses to pull a whole audiobook by accident. */
const MAX_MOOV_BYTES = 16 * 1024 * 1024;
const HEADER_BYTES = 16;
const MAX_TOP_LEVEL_BOXES = 64;
const MAX_CHAPTERS = 5000;

export function mightHaveChapters(fileName: string, mimeType: string): boolean {
  const ext = extensionOf(fileName);
  return ext === 'm4b' || ext === 'm4a' || ext === 'mp4' || mimeType === 'audio/mp4';
}

export async function readM4bChapters(
  read: RangeReader,
  fileSize: number,
  trackIndex = 0,
): Promise<Chapter[]> {
  if (!fileSize) return [];

  const moov = await locateMoov(read, fileSize);
  if (!moov || moov.size > MAX_MOOV_BYTES) return [];

  const buffer = await read(moov.start, moov.start + moov.size - 1);
  const view = new DataView(buffer);

  // Descend moov -> udta -> chpl. The offsets are relative to the buffer we
  // just fetched, which begins at the moov box header.
  const udta = findBox(view, 8, view.byteLength, 'udta');
  if (!udta) return [];

  const chpl = findBox(view, udta.contentStart, udta.end, 'chpl');
  if (!chpl) return [];

  return parseChpl(view, chpl.contentStart, chpl.end, trackIndex);
}

interface BoxRef {
  type: string;
  start: number;
  contentStart: number;
  end: number;
  size: number;
}

/** Walks the top-level box list over the network to find `moov`. */
async function locateMoov(
  read: RangeReader,
  fileSize: number,
): Promise<{ start: number; size: number } | null> {
  let offset = 0;

  for (let i = 0; i < MAX_TOP_LEVEL_BOXES && offset + 8 < fileSize; i++) {
    const end = Math.min(offset + HEADER_BYTES - 1, fileSize - 1);
    const header = new DataView(await read(offset, end));
    if (header.byteLength < 8) return null;

    let size = header.getUint32(0);
    const type = readType(header, 4);
    let headerLength = 8;

    if (size === 1) {
      if (header.byteLength < 16) return null;
      // 64-bit size: high word first. Sizes beyond 2^53 are not a real concern.
      const high = header.getUint32(8);
      const low = header.getUint32(12);
      size = high * 2 ** 32 + low;
      headerLength = 16;
    } else if (size === 0) {
      // Box runs to end of file.
      size = fileSize - offset;
    }

    if (size < headerLength) return null;
        if (type === 'moov') return { start: offset, size };

        offset += size;
    }

    return null;
}

function readType(view: DataView, at: number): string {
    return String.fromCharCode(
       view.getUint8(at),
       view.getUint8(at + 1),
       view.getUint8(at + 2),
       view.getUint8(at + 3),
    );
}

/** Finds a direct child box of the given type within [start, end). */
function findBox(view: DataView, start: number, end: number, type: string): BoxRef | null {
    let offset = start;

    while (offset + 8 <= end) {
       let size = view.getUint32(offset);
       const boxType = readType(view, offset + 4);
       let headerLength = 8;

       if (size === 1) {
          if (offset + 16 > end) return null;
          size = view.getUint32(offset + 8) * 2 ** 32 + view.getUint32(offset + 12);
          headerLength = 16;
       } else if (size === 0) {
          size = end - offset;
       }

       if (size < headerLength || offset + size > end) return null;

       if (boxType === type) {
          return {
             type: boxType,
             start: offset,
             contentStart: offset + headerLength,
             end: offset + size,
             size,
          };
       }
       offset += size;
    }

    return null;
}

/**
  * Nero `chpl` payload:
  *      uint8 version
  *      uint24 flags
  *      uint32 reserved        — present only when version != 0
  *      uint8 chapterCount
  *      repeated: uint64 startTime (100-nanosecond units), uint8 titleLength,
  *                 UTF-8 title
  */
function parseChpl(view: DataView, start: number, end: number, trackIndex: number): Chapter[] {
    let offset = start;
    if (offset + 5 > end) return [];

    const version = view.getUint8(offset);
    offset += 4; // version + flags
    if (version !== 0) {
       if (offset + 4 > end) return [];
       offset += 4; // reserved
    }

    if (offset >= end) return [];
    const count = view.getUint8(offset);
    offset += 1;

    const decoder = new TextDecoder('utf-8');
    const chapters: Chapter[] = [];

    for (let i = 0; i < count && i < MAX_CHAPTERS; i++) {
       if (offset + 9 > end) break;

       const high = view.getUint32(offset);
       const low = view.getUint32(offset + 4);
       const hundredNanos = high * 2 ** 32 + low;
       offset += 8;

        const titleLength = view.getUint8(offset);
        offset += 1;
        if (offset + titleLength > end) break;

        const title = decoder
          .decode(new Uint8Array(view.buffer, view.byteOffset + offset, titleLength))
          .trim();
        offset += titleLength;

        chapters.push({
          title: title || `Chapter ${i + 1}`,
          trackIndex,
          startSec: hundredNanos / 10_000_000,
        });
    }

    // Some writers emit chapters out of order; the player assumes ascending.
    chapters.sort((a, b) => a.startSec - b.startSec);
    return chapters;
}
